/*
 * Copyright (c) 2024 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { CanMatchFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { TermsAndConditionsAgreementStatusModel, TermsAndConditionsTypeEnum } from '@well/domain';
import { WellContextService } from '@well/shared-providers';
import { TermsAndConditionsResourceService } from '@well/resource-services';
import { TermsAndConditionsLocalStorageService } from './terms-and-conditions-local-storage.service';
import { errorUrl, termsAndConditionsUrl } from '@well/navigation';
import * as moment from 'moment';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Rights } from '@well/rights';

export const termsAndConditionsAcceptedGuard: CanMatchFn = () => {
  const router = inject(Router);
  const termsAndConditionsService = inject(TermsAndConditionsResourceService);
  const wellContextService = inject(WellContextService);
  const termsAndConditionsLocalStorageService = inject(TermsAndConditionsLocalStorageService);

  const whitelistAuthType = ['authPasswordRecovery', 'authEmailConfirmation'];

  const loggedInContext = wellContextService.getLoggedInContext();

  const returnUrl = router.getCurrentNavigation()?.initialUrl.toString();

  if (termsAndConditionsLocalStorageService.retrieve()) {
    return true;
  } else {
    return loggedInContext.pipe(
      switchMap((loggedInContext) => {
        const { loggedInUser } = loggedInContext;

        const isWhitelistedAuthType = whitelistAuthType.includes(loggedInUser?.authType);
        const hasAdminRight = loggedInUser?.authorities?.includes(Rights.ROLE_ADMIN);
        const isPlatformAdmin = loggedInUser?.authorities?.includes(Rights.ROLE_SELECT_ITEMS_ADMIN);

        if (!isWhitelistedAuthType && !hasAdminRight && !isPlatformAdmin) {
          return termsAndConditionsService
            .getUnacceptedLatestForUserOrEmail(
              loggedInUser?.uuid ?? loggedInUser?.name,
              TermsAndConditionsTypeEnum.PLATFORM
            )
            .pipe(
              map((termsAndConditionsResource: any) => {
                if (!termsAndConditionsResource?.data) {
                  const termsAndConditionsAgreementStatus: TermsAndConditionsAgreementStatusModel =
                    new TermsAndConditionsAgreementStatusModel();
                  termsAndConditionsAgreementStatus.lastRequestTimestamp = moment().valueOf();
                  termsAndConditionsAgreementStatus.documentType = TermsAndConditionsTypeEnum.PLATFORM;
                  termsAndConditionsLocalStorageService.store(termsAndConditionsAgreementStatus);
                  return true;
                }
                router.navigate(termsAndConditionsUrl, {
                  queryParams: { returnUrl: returnUrl },
                  queryParamsHandling: 'merge',
                });
                return false;
              }),
              catchError(() => of(router.createUrlTree(errorUrl)))
            );
        }
        return of(true);
      })
    );
  }
};
